import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import {PersonLogin, RoleName} from '../person/person-login';
import {SelectionItem} from '../selections/selection-item';
import {OrganizationService} from '../services/organization-service';
import {PersonLoginService} from '../services/person-login-service';
import {SelectionService} from '../services/selection-service';
import {StringUtils} from '../utils/string-utils';
import {Organization} from './organization';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DeactivateRepDialogComponent} from '../dialogs/deactivate-rep-dialog/deactivate-rep-dialog.component';
import {RepDeactivation} from '../model/rep-deactivation';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import { DatePipe } from '@angular/common';
import {BrandID} from "../enums/brand-id";

@Component({
    selector: 'organization-detail',
    templateUrl: './organization-detail.component.html',
    styleUrls: ['./organization-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, MatProgressSpinner, DatePipe]
})
export class OrganizationDetailComponent implements OnChanges, OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly organizationService = inject(OrganizationService);
  private readonly router = inject(Router);
  private readonly selectionService = inject(SelectionService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  readonly organizationId = input.required<string>({ alias: 'organization-id' });


  public personLogin!: PersonLogin;
  public readonly organization = signal<Organization>(undefined!);
  public readonly isNew = signal<boolean>(false);
  public readonly isBusy = signal<boolean>(false);
  public readonly errorMessage = signal<string>(undefined!);
  public readonly primaryContacts = signal<SelectionItem[]>(undefined!);
  public readonly responsibilities = signal<SelectionItem[]>(undefined!);
  public readonly applicationEngineers = signal<SelectionItem[]>(undefined!);
  public readonly serviceReps = signal<SelectionItem[]>(undefined!);
  public readonly controllingOrgs = signal<SelectionItem[]>(undefined!);
  public readonly organizationTypes = signal<SelectionItem[]>(undefined!);
  public readonly priceLists = signal<SelectionItem[]>(undefined!);
  public readonly installTypes = signal<SelectionItem[]>(undefined!);
  public readonly paymentTerms = signal<SelectionItem[]>(undefined!);
  public readonly deliveryTerms = signal<SelectionItem[]>(undefined!);
  public readonly transportationTerms = signal<SelectionItem[]>(undefined!);
  public readonly freightTerms = signal<SelectionItem[]>(undefined!);
  public readonly regionalTeams = signal<SelectionItem[]>(undefined!);
  public readonly orgStatusCodes = signal<SelectionItem[]>(undefined!);
  public readonly siteStatusCodes = signal<SelectionItem[]>(undefined!);
  public readonly customerStatusCodes = signal<SelectionItem[]>(undefined!);
  public readonly channelStatusCodes = signal<SelectionItem[]>(undefined!);
  public readonly parentOrgs = signal<SelectionItem[]>(undefined!);
  public isInternalOrRsm!: boolean;
  public isAdminUser!: boolean;

  private initialize(): void {
    this.isNew.set(false);
    this.loadSelectionLists();
    this.initializeFromRouteParams();
  }

  public showAddresses(): void {
    this.router.navigate(['organization/addresses/' + this.organization().orgUnitId]);
  }

  public showPhones(): void {
    this.router.navigate(['organization/phones/' + this.organization().orgUnitId]);
  }

  public showEmails(): void {
    this.router.navigate(['organization/emails/' + this.organization().orgUnitId]);
  }

  public showCustomerPolicies(): void {
    this.router.navigate(['organization/policies/' + this.organization().orgUnitId]);
  }

  public showBrands(): void {
    this.router.navigate(['organization/brands/' + this.organization().orgUnitId]);
  }

  private loadSelectionLists(): void {
    this.selectionService.getOrganizationTypes().subscribe(result => this.organizationTypes.set(result));
    this.selectionService.getPriceLists(BrandID.CLM).subscribe(result => this.priceLists.set(result));
    this.selectionService.getRegionalSalesManagers().subscribe(result => {
      this.primaryContacts.set(JSON.parse(JSON.stringify(result)));
      this.responsibilities.set(JSON.parse(JSON.stringify(result)));
    });
    this.selectionService.getApplicationEngineers().subscribe({next: result => this.applicationEngineers.set(result)});
    this.selectionService.getVendorStatusCodes().subscribe(result => this.installTypes.set(result));
    this.selectionService.getPaymentTerms().subscribe(result => this.paymentTerms.set(result));
    this.selectionService.getDeliveryTerms().subscribe(result => this.deliveryTerms.set(result));
    this.selectionService.getCustomerServiceReps().subscribe(result => this.serviceReps.set(result));
    this.selectionService.getControllingOrgs().subscribe(result => this.controllingOrgs.set(result));
    this.selectionService.getTransportationTerms().subscribe(result => this.transportationTerms.set(result));
    this.selectionService.getFreightTerms().subscribe(result => this.freightTerms.set(result));
    this.selectionService.getRegionalTeams().subscribe(result => this.regionalTeams.set(result));
    this.selectionService.getOrgStatusCodes().subscribe(result => this.orgStatusCodes.set(result));
    this.selectionService.getSiteStatusCodes().subscribe(result => this.siteStatusCodes.set(result));
    this.selectionService.getCustomerStatusCodes().subscribe(result => this.customerStatusCodes.set(result));
    this.selectionService.getChannelStatusCodes().subscribe(result => this.channelStatusCodes.set(result));
    this.selectionService.getActiveRepOffices().subscribe(repOffices => {
      this.selectionService.getActiveRegionOrgs().subscribe(regions => {
        const results: SelectionItem[] = [];
        results.push(...repOffices);
        results.push(...regions);
        this.parentOrgs.set(results);
      });
    });
  }

  private startNewOrganization(): void {
    this.organizationService.getOrganization(this.personLogin.person.primaryOrgUnitId).subscribe(primaryOrg => {
      this.organization.set(new Organization());
      this.organization().active = true;
      this.organization().controllingOrgId = primaryOrg.orgUnitId;
      this.organization().controllingOrgDescription = primaryOrg.description;
      this.organization().regionID = primaryOrg.regionID;
      this.organization().salesmanID = primaryOrg.salesmanID;
      this.organization().prnCode = primaryOrg.prnCode;
      this.organization().mnemonic = primaryOrg.mnemonic;
      this.organization().priceListId = primaryOrg.priceListId;
      this.organization().priceListDescription = primaryOrg.priceListDescription;
      this.organization().installCode = primaryOrg.installCode;
      this.organization().installDescription = primaryOrg.installDescription;
      this.organization().paymentTermsCode = primaryOrg.paymentTermsCode;
      this.organization().paymentTermsDescription = primaryOrg.paymentTermsDescription;
      this.organization().deliveryTermsCode = primaryOrg.deliveryTermsCode;
      this.organization().deliveryTermsDescription = primaryOrg.deliveryTermsDescription;
      this.organization().transportationTermsCode = primaryOrg.transportationTermsCode;
      this.organization().transportationTermsDescription = primaryOrg.transportationTermsDescription;
      this.organization().freightTermsCode = primaryOrg.freightTermsCode;
      this.organization().freightTermsDescription = primaryOrg.freightTermsDescription;
      this.organization().classificationCode = primaryOrg.classificationCode;
      this.organization().classificationDescription = primaryOrg.classificationDescription;
      this.organization().statusCode = primaryOrg.statusCode;
      this.organization().statusDescription = primaryOrg.statusDescription;
      this.organization().siteStatusCode = primaryOrg.siteStatusCode;
      this.organization().siteStatusDescription = primaryOrg.siteStatusDescription;
      this.organization().customerStatusCode = primaryOrg.customerStatusCode;
      this.organization().customerStatusDescription = primaryOrg.customerStatusDescription;
      this.organization().channelStatusCode = primaryOrg.channelStatusCode;
      this.organization().channelStatusDescription = primaryOrg.channelStatusDescription;
      this.organization().responsibilityId = primaryOrg.responsibilityId;
      this.organization().responsibilityDescription = primaryOrg.responsibilityDescription;
      this.organization().serviceRepId = primaryOrg.serviceRepId;
      this.organization().serviceRepDescription = primaryOrg.serviceRepDescription;
      this.organization().typeCode = 'CUST';
      this.organization().typeDescription = 'IEC Direct Sale Customer - Commission Only';
      this.organization().pbcId = primaryOrg.pbcId;
      this.organization().pbcDescription = primaryOrg.pbcDescription;
      this.organization().createUser = this.personLogin.person.userLogin;
      this.organization().createTimestamp = new Date();
      this.isNew.set(true);
    });
  }

  backToOrganizationMaster(): void {
    this.router.navigate(['organization']);
  }

  public hasRole(roleName: string): boolean {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  public save(): void {
    if (this.organization().typeCode !== 'REGN' && !this.organization().controllingOrgId) {
      this.errorMessage.set("Please select a Controlling Org");
    } else {
      this.isBusy.set(true);
      this.errorMessage.set(null!);
      this.organization().updateUser = this.personLogin.person.userLogin;
      let brandIDs = this.personLoginService.getBrandIDs();
      for (let brandID of brandIDs) {
        this.organizationService.save(this.organization(), brandID).subscribe(result => {
          this.isNew.set(false);
          this.isBusy.set(false);
          this.organization.set(result);
        }, error => this.handleError(error));
      }
    }
  }

  private handleError(error: any) {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.personLogin = this.personLoginService.getCachedLogin()!;
      this.isInternalOrRsm = this.personLoginService.isInternalOrRsm();
      this.isAdminUser = this.personLoginService.hasRole(RoleName.IT);
      this.initialize();
    }
  }

  openDeactivateRepDialog(): void {
    if (!this.isNew() && this.isAdminUser) {
      const deactivateRepDialog = this.dialog.open(DeactivateRepDialogComponent, {
        height: '480px',
        width: '640px',
        data: {organization: this.organization()},
        disableClose: true
      });
      deactivateRepDialog.afterClosed().subscribe({
        next: (deactivation: RepDeactivation) => {
          if (deactivation && deactivation.newRepId && deactivation.newRepId.length > 0) {
            this.organization().replacedByOrgUnitID = deactivation.newRepId;
            this.organization().cancelEffectiveDate = deactivation.cancelDate;
            this.isBusy.set(true);
            let brandIDs = this.personLoginService.getBrandIDs();
            for (let brandID of brandIDs) {
              this.organizationService.deactivate(this.organization(), brandID).subscribe({
                next: (response: string) => {
                  this.organizationService.getOrganization(this.organization().orgUnitId).subscribe({
                    next: (result: Organization) => {
                      this.organization.set(result);
                      this.isBusy.set(false);
                      this.snackBar.open(response, 'Close', {duration: 5000});
                    },
                    error: (error) => this.handleError(error),
                  });
                },
                error: (error) => this.handleError(error),
              });
            }
          }
        },
        error: (error) => this.handleError(error)
      });
    }
  }


  private initializeFromRouteParams(): void {
    let idString: string = this.organizationId();
    if (idString && idString.length > 0) {
      if ("new" === idString) {
        this.startNewOrganization();
      } else {
        this.organizationService.getOrganization(idString).subscribe(result => {
          let userOrg = this.personLogin.person.primaryOrgUnitId;
          if (this.personLogin.roles.length === 0 && result.orgUnitId !== userOrg && result.controllingOrgId !== userOrg) {
            this.backToOrganizationMaster();
          } else {
            this.organization.set(result);
          }
        });
      }
    } else {
      this.backToOrganizationMaster();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['organizationId'].firstChange) {
      this.initializeFromRouteParams();
    }
  }
}
